import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const resolveAuthState = async (currentUser: User | null) => {
      if (!isMounted) return;

      setUser(currentUser);

      if (!currentUser) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      const isEmailAdmin = currentUser.email?.toLowerCase() === "info@wildgates.co.ke";

      try {
        const { data, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", currentUser.id)
          .eq("role", "admin")
          .maybeSingle();

        if (error) {
          console.error("Failed to load user role", error);
        }

        if (isMounted) {
          setIsAdmin(Boolean(data) || isEmailAdmin);
        }
      } catch (error) {
        console.error("Failed to resolve auth state", error);
        if (isMounted) {
          setIsAdmin(isEmailAdmin);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    const bootstrap = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        await resolveAuthState(session?.user ?? null);
      } catch (error) {
        console.error("Failed to bootstrap auth session", error);
        if (isMounted) {
          setUser(null);
          setIsAdmin(false);
          setLoading(false);
        }
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      void resolveAuthState(session?.user ?? null);
    });

    void bootstrap();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    return supabase.auth.signInWithPassword({ email, password });
  };

  const signUp = async (email: string, password: string) => {
    return supabase.auth.signUp({ email, password, options: { emailRedirectTo: window.location.origin } });
  };

  const signOut = async () => {
    return supabase.auth.signOut();
  };

  return { user, isAdmin, loading, signIn, signUp, signOut };
};
